import { useState, useEffect, useRef, useCallback } from 'react';

interface MobileToastProps {
  targetSelector?: string;
  threshold?: number;
  desktopWidth?: number; // viewport width above which toast never shows
}

const MobileToast: React.FC<MobileToastProps> = ({
  targetSelector = '.container',
  threshold = 20,
  desktopWidth = 1024,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const checkTimer = useRef<NodeJS.Timeout | null>(null);
  const closedSessionKey = 'mobile-toast-closed';

  // Check if user is on desktop (viewport width >= desktopWidth)
  const isDesktopView = useCallback(() => {
    return window.innerWidth >= desktopWidth;
  }, [desktopWidth]);

  // Check if user has explicitly closed the toast this session
  const isUserDismissed = useCallback(() => {
    return sessionStorage.getItem(closedSessionKey) === 'true';
  }, []);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    // Remember that user closed it for this session
    sessionStorage.setItem(closedSessionKey, 'true');
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
  }, []);

  // Main detection function
  const checkWrapping = useCallback(() => {
    // If desktop view, never show
    if (isDesktopView()) {
      if (isVisible) {
        handleClose();
      }
      return false;
    }

    // If user dismissed it this session, don't show
    if (isUserDismissed()) {
      if (isVisible) {
        handleClose();
      }
      return false;
    }

    const target = document.querySelector(targetSelector) as HTMLElement;
    if (!target) {
      return false;
    }

    let hasWrapping = false;

    // Check all text elements inside the target for multiple lines
    const textElements = target.querySelectorAll(
      'p, span, div, h1, h2, h3, h4, h5, h6, li, label, a, button, .whitespace-nowrap, .flex-wrap > *'
    );

    textElements.forEach((el) => {
      const rect = (el as HTMLElement).getBoundingClientRect();
      const parent = el.parentElement;
      if (!parent) return;
      const parentRect = parent.getBoundingClientRect();

      // If element is below parent's bottom, it's wrapping
      if (rect.bottom > parentRect.bottom + 5) {
        hasWrapping = true;
        return;
      }

      // For flex-wrap containers, check if child is on a new row
      const parentStyle = window.getComputedStyle(parent);
      if (parentStyle.display === 'flex' && parentStyle.flexWrap === 'wrap') {
        const children = Array.from(parent.children) as HTMLElement[];
        let prevTop: number | null = null;
        for (const child of children) {
          const childRect = child.getBoundingClientRect();
          if (prevTop !== null && childRect.top > prevTop + 5) {
            hasWrapping = true;
            return;
          }
          prevTop = childRect.top;
        }
      }
    });

    // Check horizontal overflow
    if (target.scrollWidth > target.clientWidth + threshold) {
      hasWrapping = true;
    }

    // Check overflow elements
    const overflowElements = target.querySelectorAll('[style*="overflow"], .overflow-hidden, .truncate');
    overflowElements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      if (htmlEl.scrollWidth > htmlEl.clientWidth) {
        hasWrapping = true;
      }
    });

    if (hasWrapping && !isVisible && !isClosing) {
      console.log('[MobileToast] Wrapping detected – showing toast');
      setIsVisible(true);
    } else if (!hasWrapping && isVisible) {
      console.log('[MobileToast] No wrapping – hiding toast');
      handleClose();
    }

    return hasWrapping;
  }, [targetSelector, threshold, desktopWidth, isDesktopView, isUserDismissed, isVisible, isClosing, handleClose]);

  // Setup detection
  useEffect(() => {
    // Clear dismissal flag on page load (optional)
    // If you want to persist dismissal across page loads, use localStorage
    // but for now we'll keep it session-based only

    // Initial check after mount
    const initialCheck = setTimeout(() => checkWrapping(), 300);
    clearTimeout(initialCheck);

    const target = document.querySelector(targetSelector);
    let resizeObserver: ResizeObserver | null = null;
    if (target) {
      resizeObserver = new ResizeObserver(() => checkWrapping());
      resizeObserver.observe(target);
    }

    const bodyObserver = new ResizeObserver(() => checkWrapping());
    bodyObserver.observe(document.body);

    const handleResize = () => checkWrapping();
    window.addEventListener('resize', handleResize);

    const handleScroll = () => checkWrapping();
    window.addEventListener('scroll', handleScroll);

    // Periodic check as fallback (every 3 seconds)
    const intervalId = setInterval(() => {
      if (!isVisible && document.visibilityState === 'visible') {
        checkWrapping();
      }
    }, 3000);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      bodyObserver.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      clearInterval(intervalId);
      if (checkTimer.current) clearTimeout(checkTimer.current);
    };
  }, [checkWrapping, isVisible]);

  // Swipe to close
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (diff < -50) {
      handleClose();
    }
    touchStartX.current = null;
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed bottom-16 left-0 right-0 z-50 mx-2 sm:mx-4 transition-all duration-300 ${
        isClosing ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
      }`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg shadow-xl p-3 flex items-center justify-between gap-2 max-w-md mx-auto">
        <div className="flex items-start gap-2 flex-1">
          <span className="text-yellow-600 text-xl flex-shrink-0">📱</span>
          <div>
            <p className="text-sm text-gray-800 font-medium">Better experience on desktop</p>
            <p className="text-xs text-gray-600">Some content may wrap. Try viewing on a larger screen.</p>
          </div>
        </div>
        <button
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600 transition p-1 flex-shrink-0 touch-target"
          aria-label="Close notification"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default MobileToast;